/**
 * Category Service - Backend API integration for categories
 *
 * Endpoints (relative to CATEGORIES_URL):
 * GET    /api/categories          - List all categories
 * GET    /api/categories/:id      - Get single category
 * POST   /api/categories          - Create category (admin)
 * PATCH  /api/categories/reorder  - Persist drag-and-drop priority order (admin)
 * PATCH  /api/categories/:id      - Update category (admin)
 * DELETE /api/categories/:id      - Delete category (admin)
 * POST   /api/categories/seed     - Seed default categories (admin)
 */

import api from './api';

// Single source of truth for the category endpoint base path, so the URL isn't
// hardcoded in every method.
const CATEGORIES_URL = '/categories';

const categoryService = {
  /**
   * Get all categories
   */
  getAll: async () => {
    const response = await api.get(CATEGORIES_URL);
    return response.data;
  },

  /**
   * Get single category by ID
   */
  getById: async (id) => {
    const response = await api.get(`${CATEGORIES_URL}/${id}`);
    return response.data;
  },

  /**
   * Create a new category (admin only)
   * @param {Object} categoryData - { name, description, imageUrl }
   */
  create: async (categoryData) => {
    const response = await api.post(CATEGORIES_URL, {
      name: categoryData.name,
      description: categoryData.description || '',
      imageUrl: categoryData.imageUrl || null,
    });
    return response.data;
  },

  uploadImage: async (id, file) => {
    if (!id) {
      throw new Error('Category ID is required');
    }
    if (!file) {
      throw new Error('Please select an image');
    }
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`${CATEGORIES_URL}/${id}/image/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Update a category (admin only)
   */
  update: async (id, updates) => {
    const cleanPayload = {};
    if (updates.name !== undefined) cleanPayload.name = updates.name;
    if (updates.description !== undefined) cleanPayload.description = updates.description;
    if (updates.imageUrl !== undefined) cleanPayload.imageUrl = updates.imageUrl;
    if (updates.sortOrder !== undefined) cleanPayload.sortOrder = updates.sortOrder;
    const response = await api.patch(`${CATEGORIES_URL}/${id}`, cleanPayload);
    return response.data;
  },

  /**
   * Delete a category (admin only)
   */
  delete: async (id) => {
    const response = await api.delete(`${CATEGORIES_URL}/${id}`);
    return response.data;
  },

  /**
   * Persist a drag-and-drop reorder (admin only). Ranks are assigned 1..N in
   * the order of `orderedIds`; "Uncategorized" must not be included.
   * @param {number[]} orderedIds - category ids, top of the list first
   * @returns {Promise<Array>} the full category list in the new order
   */
  reorder: async (orderedIds) => {
    const response = await api.patch(`${CATEGORIES_URL}/reorder`, { orderedIds });
    return response.data;
  },

  /**
   * Seed default categories (admin only)
   * Creates: Fruits, Vegetables, Organic, Exotic
   */
  seed: async () => {
    const response = await api.post(`${CATEGORIES_URL}/seed`);
    return response.data;
  }
};

export default categoryService;
